<script lang="ts">
export default {
  name: 'StoreConnectMapionbiz',
}
</script>
<script setup lang="ts">
/** Import */
import { computed, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import {
  KkButton,
  KkFormLabel,
  KkTextField,
  KkValidationError,
} from '../../components/base'
import {
  KkInfo,
} from '../../components/ui'

/** Interface */
interface ReadLocationStoreParams {
  company_uuid: string
  store_uuid: string
}

/** Variable */
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const emit = defineEmits(['update-title'])

/** Computed */
const companyUuid = computed<string>(() => route.params.company_uuid as string)

const storeUuid = computed<string>(() => route.params.store_uuid as string)

const submittable = computed<boolean>(() => {
  const { dirty, valid } = meta.value
  return dirty && valid
})

const savedMapionbizId = computed<number>(() => {
  if (!locationStoreOcMapionBiz.value) return null
  return locationStoreOcMapionBiz.value.mapionBizId
})

/** Form */
const validationSchema = {
  mapionbizId: 'required',
}

const { handleSubmit, meta } = useForm({
  validationSchema,
  initialValues: {
    mapionbizId: '',
  },
})

const {
  value: mapionbizId,
  errorMessage: mapionbizIdError,
  handleChange: updateShufooId
} = useField<string>('mapionbizId', undefined, { label: t('pages.LocationStoreConnectMapionbiz.form.mapionbizId') })

/** Store: state */
const locationStore = computed(() => store.state.companiesLocationStore.data)
const locationStoreOcMapionBiz = computed(() => store.state.companiesStoreOcMapionBiz.data)
const loading = computed<boolean>(() => {
  const { companiesLocationStore, companiesStoreOcMapionBiz, companiesAccessibleApi } = store.state
  return companiesLocationStore.loading || companiesStoreOcMapionBiz.loading || companiesAccessibleApi.loading
})

/** Store: getter */
const isAdmin = computed<boolean>(() => store.getters['companiesAccessibleApi/isAdmin'])
const isDistributer = computed<boolean>(() => store.getters['companiesAccessibleApi/isDistributer'])
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])

/** Store: action */
const readLocationStore = (payload: ReadLocationStoreParams) => store.dispatch('companiesLocationStore/readStore', payload)
const readStoreOcMapionBiz = () => store.dispatch('companiesStoreOcMapionBiz/readStoreOcMapionBiz')
const createLocationStoreOcMapionBiz = (mapionbizId: string) => store.dispatch('companiesStoreOcMapionBiz/createStoreOcMapionBiz', mapionbizId)

/** Lifecycle */
readLocationStore({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
readStoreOcMapionBiz()

/** Watch */
watch(locationStore, () => {
  if (!locationStore.value) return
  emit('update-title', t('pages.LocationStoreConnectMapionbiz.title', { name: locationStore.value.attributes.name }))
})

/** WatchEffect */
watchEffect(() => {
  if (loading.value) return
  const canDisplayShufoo = showOnlyOwnerAuthority.value && (isAdmin.value || isDistributer.value)
  if (!canDisplayShufoo) {
    router.push({
      name: 'LocationStoreConnects',
      params: { company_uuid: companyUuid.value, store_uuid: storeUuid.value }
    })
  }
})

/** Methods */
const onSubmit = handleSubmit(async value => {
  await createLocationStoreOcMapionBiz(value.mapionbizId)
  router.push({
    name: 'LocationStoreConnects',
    params: { company_uuid: companyUuid.value, store_uuid: storeUuid.value }
  })
})
</script>

<template>
  <div class="storeMapionbizConnect">
    <div class="storeMapionbizConnect__info">
      <KkInfo icon="info">
        {{ $t('pages.LocationStoreConnectMapionbiz.onlyDistributorOrAdmin') }}
      </KkInfo>
    </div>
    <p v-if="savedMapionbizId">
      {{ $t('pages.LocationStoreConnectMapionbiz.connectedWith', { mapionbizId: savedMapionbizId }) }}
    </p>
    <form v-else @submit.prevent="onSubmit">
      <KkFormLabel :text="t('pages.LocationStoreConnectMapionbiz.form.mapionbizId')" />
      <KkTextField :model-value="mapionbizId" class="c-textField" :placeholder="$t('messages.noSettings')" @update:model-value="updateShufooId" />
      <KkValidationError :text="mapionbizIdError" />
      <div class="storeMapionbizConnect__buttons">
        <KkButton
          color="secondary"
          :to="{ name: 'LocationStoreConnects', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
          :text="$t('labels.cancel')"
        />
        <KkButton
          type="submit"
          color="primary"
          :text="$t('labels.save')"
          :disabled="!submittable || loading"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../stylesheets/biz_spa/foundation/mixin";
.storeMapionbizConnect {
  padding: 16px;
  border-radius: 4px;
  background-color: #FFF;
  @include mq() {
    padding: 32px;
  }
  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 16px;
    @include mq() {
      margin-top: 32px;
    }
  }
  &__info {
    margin-bottom: 16px;
    @include mq() {
      margin-bottom: 32px;
    }
  }
}
</style>
